import React, {
	memo,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
	type ClipboardEvent,
	type ElementRef,
	type KeyboardEvent,
	type PropsWithChildren,
} from 'react';
import flatten from 'lodash/flatten';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Box, Flex, Pressable, xcss } from '@atlaskit/primitives';
import type { OptionType, GroupType } from '@atlaskit/select';
import SimpleTag from '@atlaskit/tag/simple-tag';
import Tooltip from '@atlaskit/tooltip';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { TagListWrapper } from '@atlassian/jira-business-fields/src/common/ui/tag-list-wrapper/index.tsx';
import {
	StatusCategoryTypes,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { getBoardId } from '@atlassian/jira-common-navigation/src/get-scope-data/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useSprintField } from '@atlassian/jira-issue-field-sprint/src/services/sprint-field/index.tsx';
import type { SprintValue } from '@atlassian/jira-issue-shared-types/src/common/types/sprint-type.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import { CellContainer } from '../../../common/ui/list-cell/styled.tsx';
import { useHierarchyStore } from '../../../controllers/hierarchy/hierarchy-store/index.tsx';
import messages from './messages.tsx';
import { SprintPopupContent } from './popup-content/index.tsx';
import { mapOptionToSprint } from './popup-content/utils.tsx';
import type { SprintCellProps } from './types.tsx';

const POPUP_OFFSET: [number, number] = [0, 1 - DEFAULT_ROW_HEIGHT];
const POPUP_OFFSET_NEW: [number, number] = [0, -DEFAULT_ROW_HEIGHT - 1];

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const SprintCell = memo<SprintCellProps>(
	({
		fieldKey,
		issueKey,
		issueId,
		isEditable,
		fieldValue,
		width,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		issueTypeLevel,
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		inputValue,
		onInputChange,
		cellRef,
	}: SprintCellProps) => {
		const { formatMessage } = useIntl();
		const boardId = getBoardId(typeof window !== 'undefined' ? window.location.href : '');
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);

		const [options, setOptions] = useState<GroupType<OptionType>[]>([]);
		const [{ issuesMap }] = useHierarchyStore();

		const initialValue = useMemo(() => {
			if (fieldValue && Array.isArray(fieldValue) && fieldValue.length) {
				const initialSprintValue = fieldValue[0];
				if (initialSprintValue) {
					return [
						{
							id: Number(initialSprintValue.sprintId),
							name: String(initialSprintValue.name),
							state: String(initialSprintValue.state),
							boardId: Number(boardId),
						},
					];
				}
			}
			return undefined;
		}, [boardId, fieldValue]);

		const [{ value: sprintValue, error }, { saveValue }] = useSprintField({
			fieldKey,
			initialValue,
			onSuccess: noop,
			onFailure: noop,
			issueKey: toIssueKey(issueKey),
			issueId,
		});
		const currentValue = sprintValue.length ? sprintValue[0] : undefined;

		const [selectValue, setSelectValue] = useState(currentValue);
		const clearOnBlur = useRef(false);

		if (
			currentValue?.id !== selectValue?.id &&
			!editMode &&
			fg('jwm_list_new_copy_paste_behaviour')
		) {
			setSelectValue(currentValue);
		}

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleClick = useCallback(() => {
			if (isEditable && !editMode) {
				setEditMode(true);
			}
		}, [isEditable, editMode, setEditMode]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				if (!isEditable && e.key === 'Enter') {
					e.preventDefault();
				}
				!editMode && onKeyDown?.(e);
			},
			[isEditable, editMode, onKeyDown],
		);

		const handleCutCopy = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();
				if (currentValue) {
					event.clipboardData.setData('text/plain', currentValue.name);
				}

				if (event.type === 'copy') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', 'sprint');
				} else if (event.type === 'cut') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', 'sprint');
					// clear cell content
					saveValue(
						[],
						null,
						createAnalyticsEvent({}),
					).catch(/* ignore as it's caught by onFailure */);
				}
			},
			[createAnalyticsEvent, currentValue, saveValue],
		);

		const handlePaste = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();

				const sprintName = event.clipboardData.getData('text/plain');
				const allOptions = flatten(options.map((group) => group.options));
				const validOption = allOptions.find((op) => op.label === sprintName);

				if (validOption) {
					saveValue([mapOptionToSprint(validOption, boardId)], null, createAnalyticsEvent({}));
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'sprint', {
						valid: true,
					});
				} else {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'sprint', {
						valid: false,
						error: 'Paste content invalid option',
					});
				}
			},
			[options, saveValue, boardId, createAnalyticsEvent],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		const popupComponent = useMemo(
			() =>
				getPopupComponentWithHeight({
					numOptions: 0,
					hasFooter: false,
				}),
			[],
		);

		const handleClose = useCallback(() => {
			setEditMode(false);
		}, [setEditMode]);

		const saveValueNew = useCallback(
			async (value: SprintValue[], meta: null, analyticsEvent: UIAnalyticsEvent) => {
				setSelectValue(value[0]);
				if (value[0] != null) {
					saveValue(value, meta, analyticsEvent);
					clearOnBlur.current = false;
				} else {
					clearOnBlur.current = true;
				}
			},
			[saveValue],
		);

		const handleKeydown = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				if (editMode) {
					if (selectValue == null) {
						if (e.key === 'Escape') {
							clearOnBlur.current = false;
						}
						setSelectValue(currentValue);
					} else if (e.key === 'Delete') {
						saveValue([], null, createAnalyticsEvent({}));
						setEditMode(false);
						setTimeout(() => {
							wrapperRef?.current?.focus();
						});
						clearOnBlur.current = false;
					}
				}
				return false;
			},
			[
				createAnalyticsEvent,
				currentValue,
				editMode,
				saveValue,
				selectValue,
				setEditMode,
				wrapperRef,
			],
		);

		const handleBlur = useCallback(() => {
			if (clearOnBlur.current) {
				saveValue([], null, createAnalyticsEvent({}));
				clearOnBlur.current = false;
			}
			return false;
		}, [createAnalyticsEvent, saveValue]);

		const cellOverrides = useMemo(
			() => () => ({
				onKeyDown: handleKeydown,
				onBlur: handleBlur,
			}),
			[handleKeydown, handleBlur],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const popupContent = useCallback(
			() => (
				<SprintPopupContent
					wrapperRef={wrapperRef}
					width={width}
					currentSprint={fg('jira_list_keyboard_nav') ? selectValue : currentValue}
					onCloseMenu={handleClose}
					saveValue={fg('jira_list_keyboard_nav') ? saveValueNew : saveValue}
					setSprintOptions={setOptions}
					issueTypeLevel={issueTypeLevel}
					inputValue={fg('jira_list_keyboard_nav') ? inputValue : undefined}
					onInputChange={fg('jira_list_keyboard_nav') ? onInputChange : undefined}
				/>
			),
			[
				wrapperRef,
				width,
				selectValue,
				currentValue,
				handleClose,
				saveValueNew,
				saveValue,
				issueTypeLevel,
				inputValue,
				onInputChange,
			],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => {
				const sprintName = currentValue?.name;
				if (currentValue?.state === 'CLOSED' && sprintName) {
					const issue = issuesMap.get(issueId.toString());
					if (
						issue &&
						issue.status?.categoryId !== StatusCategoryIds[StatusCategoryTypes.DONE].toString()
					) {
						// closed sprints should not be shown if the status is not done
						// means that this ticket is on the backlog
						return (
							<Pressable {...triggerProps} xcss={popupTriggerStyles}>
								<TagListWrapper isEditable={isEditable} />
							</Pressable>
						);
					}

					const tooltipText = formatMessage(messages.completedSprintTooltip, {
						sprintName,
					});

					return (
						<Pressable {...triggerProps} xcss={popupTriggerStyles}>
							<TagListWrapper isEditable={isEditable}>
								<Tooltip content={tooltipText} position="top">
									<SimpleTag
										text={sprintName}
										color="greenLight"
										elemBefore={
											<Box xcss={iconWrapperStyles}>
												<CheckCircleOutlineIcon size="small" label={tooltipText} />
											</Box>
										}
									/>
								</Tooltip>
							</TagListWrapper>
						</Pressable>
					);
				}

				return (
					<Pressable {...triggerProps} xcss={popupTriggerStyles}>
						<TagListWrapper isEditable={isEditable}>
							{currentValue && sprintName && <SimpleTag text={sprintName} />}
						</TagListWrapper>
					</Pressable>
				);
			},
			[currentValue, formatMessage, isEditable, issueId, issuesMap],
		);

		const cellContent = (
			<>
				<Popup
					isOpen={editMode}
					placement="bottom-start"
					autoFocus={false}
					offset={fg('jira_list_keyboard_nav') ? POPUP_OFFSET_NEW : POPUP_OFFSET}
					onClose={fg('jira_list_keyboard_nav') ? noop : handleClose}
					popupComponent={popupComponent}
					content={popupContent}
					trigger={popupTrigger}
				/>
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable={isEditable}
					hasPadding={false}
					testId="business-list.ui.list-view.sprint-cell.cell-container"
				>
					<ContentWrapper>{cellContent}</ContentWrapper>
				</CellWrapperNew>
			);
		}

		return (
			<CellContainer
				onClick={handleClick}
				isFocused={isSelected}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				onCopy={handleCutCopy}
				onCut={handleCutCopy}
				onPaste={handlePaste}
				data-testid="business-list.ui.list-view.sprint-cell.cell-container"
			>
				{cellContent}
			</CellContainer>
		);
	},
);

export default SprintCell;

const ContentWrapper = ({ children }: PropsWithChildren) => (
	<Flex xcss={[wrapperStyles]}>{children}</Flex>
);

const iconWrapperStyles = xcss({
	marginTop: 'space.025',
	marginLeft: 'space.025',
});

const wrapperStyles = xcss({
	width: '100%',
	height: '100%',
});

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});
